import React, { useState, useEffect } from "react";
import {
  Typography,
  Container,
  Box,
  InputLabel,
  Button,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import {
  fetchMachineTypes,
  fetchMachineVariants,
  fetchTrackerDevices,
  fetchManufacturers,
  registerMachine,
} from "./api/machineApi";
import { useHistory } from "react-router-dom";

const theme = createTheme({
  components: {
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
          fontWeight: "bold",
          marginBottom: "4px",
          display: "block",
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
          padding: "4px 8px",
        },
      },
    },
  },
});

const StyledFormBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(0.5),
}));

const RequiredStar = styled("span")(({ theme }) => ({
  color: theme.palette.error.main,
}));

const RegisterMachine = () => {
  const history = useHistory();

  const [formData, setFormData] = useState({
    machineName: "",
    machineType: "",
    machineVariant: "",
    trackerDevice: "",
    manufacturerId: "",
    description: "",
    width: "",
    height: "",
    blades: "",
    seedCapacity: "",
    fertilizerCapacity: "",
    tractorHorsePower: "",
    manufacturingDate: "",
    manufacturingYear: "",
  });

  const [machineTypes, setMachineTypes] = useState([]);
  const [machineVariants, setMachineVariants] = useState([]);
  const [trackerDevices, setTrackerDevices] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      const cleanedData = {
        ...formData,
        machineType: Number(formData.machineType),
        machineVariant: Number(formData.machineVariant),
        trackerDevice: Number(formData.trackerDevice),
        manufacturerId: Number(formData.manufacturerId),
        width: parseFloat(formData.width),
        height: parseFloat(formData.height),
        blades: parseInt(formData.blades, 10),
        seedCapacity: parseInt(formData.seedCapacity, 10),
        fertilizerCapacity: parseInt(formData.fertilizerCapacity, 10),
        tractorHorsePower: parseInt(formData.tractorHorsePower, 10),
        manufacturingYear: parseInt(formData.manufacturingYear, 10),
      };

      const result = await registerMachine(cleanedData);
      console.log("Success:", result);
      alert("Machine registered successfully!");
      history.push("/machine-overview");
    } catch (error) {
      console.error("Error:", error);
      alert(error.message || "Failed to register machine");
    }
  };

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [types, variants, trackers, manufacturersData] =
          await Promise.all([
            fetchMachineTypes(),
            fetchMachineVariants(),
            fetchTrackerDevices(),
            fetchManufacturers(),
          ]);

        setMachineTypes(types || []); // Ensure it's an array
        setMachineVariants(variants || []); // Ensure it's an array
        setTrackerDevices(trackers || []); // Ensure it's an array
        setManufacturers(manufacturersData || []); // Ensure it's an array
      } catch (error) {
        console.error("Failed to fetch dropdown data:", error);
      }
    };

    fetchDropdowns();
  }, []);

  const handleCancel = () => {
    setFormData({
      machineName: "",
      machineType: "",
      machineVariant: "",
      trackerDevice: "",
      manufacturerId: "",
      description: "",
      width: "",
      height: "",
      blades: "",
      seedCapacity: "",
      fertilizerCapacity: "",
      tractorHorsePower: "",
      manufacturingDate: "",
      manufacturingYear: "",
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md">
        <Box mt={3} mb={2}>
          <Typography variant="h5" component="h1" gutterBottom>
            Register Machine
          </Typography>
          <StyledFormBox>
            <Grid container spacing={1}>
              {[
                { name: "machineName", label: "Machine Name" },
                { name: "description", label: "Description" },
                { name: "width", label: "Width (m)", type: "number" },
                { name: "height", label: "Height (m)", type: "number" },
                { name: "blades", label: "Blades", type: "number" },
                {
                  name: "seedCapacity",
                  label: "Seed Capacity (kg)",
                  type: "number",
                },
                {
                  name: "fertilizerCapacity",
                  label: "Fertilizer Capacity (kg)",
                  type: "number",
                },
                {
                  name: "tractorHorsePower",
                  label: "Tractor Horse Power",
                  type: "number",
                },
                {
                  name: "manufacturingDate",
                  label: "Manufacturing Date",
                  type: "date",
                },
                {
                  name: "manufacturingYear",
                  label: "Manufacturing Year",
                  type: "number",
                },
              ].map(({ name, label, type = "text" }) => (
                <Grid item xs={12} sm={6} key={name}>
                  <InputLabel htmlFor={name}>
                    {label} <RequiredStar>*</RequiredStar>
                  </InputLabel>
                  <TextField
                    fullWidth
                    id={name}
                    name={name}
                    type={type}
                    value={formData[name]}
                    onChange={handleChange}
                    InputLabelProps={type === "date" ? { shrink: true } : {}}
                    required
                  />
                </Grid>
              ))}

              {[
                {
                  name: "machineType",
                  label: "Machine Type",
                  options: machineTypes,
                  labelField: "machineTypeName",
                },
                {
                  name: "machineVariant",
                  label: "Variant",
                  options: machineVariants,
                  labelField: "machineVariantName",
                },
                {
                  name: "trackerDevice",
                  label: "Tracker Device",
                  options: trackerDevices,
                  labelField: "trackerDeviceBrand",
                },
                {
                  name: "manufacturerId",
                  label: "Manufacturer",
                  options: manufacturers,
                  labelField: "name",
                },
              ].map(({ name, label, options, labelField }) => (
                <Grid item xs={12} sm={6} key={name}>
                  <InputLabel htmlFor={name}>
                    {label} <RequiredStar>*</RequiredStar>
                  </InputLabel>
                  <FormControl fullWidth required>
                    <Select
                      name={name}
                      value={formData[name]}
                      onChange={handleChange}
                    >
                      <MenuItem value="">
                        <em>Select</em>
                      </MenuItem>
                      {Array.isArray(options) &&
                        options.map((opt) => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt[labelField]}{" "}
                            {/* Dynamically access the field based on labelField */}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
              <Button onClick={handleCancel}>Cancel</Button>
              <StyledButton
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                Register
              </StyledButton>
            </Box>
          </StyledFormBox>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default RegisterMachine;
